import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowsClockwise, DownloadSimple, FilmSlate, Gear, Plus, Translate, Trash, UsersFour, Waveform, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import AudioPreview from "@/components/AudioPreview";
import UploadZone from "@/components/UploadZone";
import SRTViewer from "@/components/SRTViewer";
import ShareProjectModal from "@/components/ShareProjectModal";
import { api, API_BASE, formatErr } from "@/lib/api";
import { DIALECTS, LANGUAGES } from "@/lib/languages";
import { connectProjectWS } from "@/lib/ws";

export default function ProjectPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [activeTranslationId, setActiveTranslationId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showReprocess, setShowReprocess] = useState(false);
  const [showAddTrack, setShowAddTrack] = useState(false);
  // Upload state for the inline (first) upload card.
  const [uploadProgress, setUploadProgress] = useState(null); // {pct, loaded, total}
  const [autoStart, setAutoStart] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Live job events (keyed by job_type → latest event)
  const [jobs, setJobs] = useState({});
  const [burnLink, setBurnLink] = useState(null);
  const wsDisposer = useRef(null);

  // Translate controls
  const [mode, setMode] = useState("translate");
  const [targetLang, setTargetLang] = useState("en");
  const [targetDialect, setTargetDialect] = useState("");

  // Diarize controls
  const [diarizeMethod, setDiarizeMethod] = useState("prompt");
  // Platform feature flag — fetched from /settings (admin-controlled).
  const [featureDiarize, setFeatureDiarize] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/settings").then(({ data }) => {
      if (alive) setFeatureDiarize(!!data?.feature_diarization_enabled);
    }).catch(() => { /* non-fatal: keep default off */ });
    return () => { alive = false; };
  }, []);

  const dialectsForTarget = useMemo(() => DIALECTS[targetLang] || [], [targetLang]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      // Keep the active track if it still exists, else pick the first
      setActiveTrackId((prev) => {
        const tracks = data.tracks || [];
        if (prev && tracks.some((t) => t.track_id === prev)) return prev;
        return tracks[0]?.track_id || null;
      });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  // Active track + translation derived state
  const activeTrack = useMemo(() => {
    if (!project) return null;
    return (project.tracks || []).find((t) => t.track_id === activeTrackId) || null;
  }, [project, activeTrackId]);

  // Reset active translation when switching tracks
  useEffect(() => {
    if (!activeTrack) { setActiveTranslationId(null); return; }
    setActiveTranslationId((prev) => {
      const list = activeTrack.translations || [];
      if (prev && list.some((t) => t.translation_id === prev)) return prev;
      return list.length ? list[list.length - 1].translation_id : null;
    });
  }, [activeTrack]);

  // WebSocket — subscribe for live job progress
  useEffect(() => {
    if (!id) return undefined;
    wsDisposer.current = connectProjectWS(id, (event) => {
      setJobs((prev) => ({ ...prev, [event.job_type]: event }));
      if (event.status === "done") {
        // Refresh project on any completed job
        load();
        if (event.job_type === "translate" && event.payload?.translation_id) {
          setActiveTranslationId(event.payload.translation_id);
          if (event.payload.track_id) setActiveTrackId(event.payload.track_id);
        }
        if (event.job_type === "transcribe" && event.payload?.track_id) {
          setActiveTrackId(event.payload.track_id);
        }
        if (event.job_type === "burn" && event.payload?.download_url) {
          setBurnLink({
            url: (process.env.REACT_APP_BACKEND_URL + event.payload.download_url),
            filename: event.payload.filename,
          });
        }
      }
      if (event.status === "failed") {
        toast.error(event.message || "Job failed");
      }
      if (event.status === "cancelled") {
        toast.success("Job cancelled");
        load();
      }
    });
    return () => { if (wsDisposer.current) wsDisposer.current(); };
  }, [id, load]);

  useEffect(() => {
    if (mode === "dialect" && project) setTargetLang(project.source_language);
  }, [mode, project]);

  const transcribeJob = jobs.transcribe;
  const translateJob = jobs.translate;
  const diarizeJob = jobs.diarize;
  const burnJob = jobs.burn;

  const isTranscribing =
    transcribeJob && ["queued", "running"].includes(transcribeJob.status);
  const isTranslating =
    translateJob && ["queued", "running"].includes(translateJob.status);
  const isDiarizing =
    diarizeJob && ["queued", "running"].includes(diarizeJob.status);
  const isBurning =
    burnJob && ["queued", "running"].includes(burnJob.status);

  const uploadAudio = async (uploadFile, trackTitle, opts = {}) => {
    const target = uploadFile || file;
    if (!target) { toast.error("Choose an audio file first"); return null; }
    const shouldAutoStart = opts.autoStart ?? autoStart;
    try {
      setUploading(true);
      setUploadProgress({ pct: 0, loaded: 0, total: target.size });
      const form = new FormData();
      form.append("file", target);
      if (trackTitle) form.append("track_title", trackTitle);
      form.append("auto_start", shouldAutoStart ? "true" : "false");
      form.append("language", project.source_language);
      const { data } = await api.post(`/projects/${id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0, // long uploads should not timeout
        onUploadProgress: (e) => {
          const total = e.total || target.size;
          const loaded = e.loaded || 0;
          setUploadProgress({ pct: Math.min(99, Math.round((loaded / total) * 100)), loaded, total });
        },
      });
      setUploadProgress({ pct: 100, loaded: target.size, total: target.size });
      if (data?.transcribe_error) {
        toast.error(`Uploaded, but couldn't start transcription: ${data.transcribe_error}`);
      } else if (shouldAutoStart) {
        toast.success("Uploaded — transcription started");
      } else {
        toast.success("Uploaded — click Transcribe when ready");
      }
      setFile(null);
      await load();
      if (data?.track_id) setActiveTrackId(data.track_id);
      // brief pause before hiding the bar so user sees 100%
      setTimeout(() => setUploadProgress(null), 800);
      return data;
    } catch (e) {
      setUploadProgress(null);
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const cancelJob = async (jobId) => {
    if (!jobId) return;
    try {
      await api.post(`/projects/${id}/jobs/${jobId}/cancel`);
      toast.success("Cancelling job…");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const startTranscribe = async (trackId) => {
    const tid = trackId || activeTrack?.track_id;
    if (!tid) return;
    try {
      await api.post(`/projects/${id}/tracks/${tid}/transcribe`);
      toast.success("Transcription started");
      // Optimistic flip: reload happens via WS, but show queued immediately
      setProject((p) => {
        if (!p) return p;
        const idx = (p.tracks || []).findIndex((t) => t.track_id === tid);
        if (idx < 0) return p;
        const next = { ...p, tracks: [...p.tracks] };
        next.tracks[idx] = { ...next.tracks[idx], status: "queued", error: null, last_job_progress: 0, last_job_message: "Queued…" };
        return next;
      });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const translate = async () => {
    if (!activeTrack?.segments?.length) { toast.error("Transcribe audio first"); return; }
    try {
      await api.post(`/projects/${id}/translate`, {
        track_id: activeTrack.track_id,
        target_language: targetLang,
        target_dialect: targetDialect || null,
        mode,
      });
      toast.success("Translation started");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const diarize = async () => {
    if (!activeTrack) return;
    try {
      await api.post(`/projects/${id}/diarize`, {
        track_id: activeTrack.track_id,
        method: diarizeMethod,
        translation_id: activeTranslationId || null,
      });
      toast.success("Diarization started");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const burn = async () => {
    if (!videoFile) { toast.error("Choose a video file"); return; }
    if (!activeTrack) return;
    try {
      const form = new FormData();
      form.append("video", videoFile);
      form.append("track_id", activeTrack.track_id);
      if (activeTranslationId) form.append("translation_id", activeTranslationId);
      form.append("include_speakers", "false");
      await api.post(`/projects/${id}/burn`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60 * 1000,
      });
      toast.success("Render started");
      setBurnLink(null);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const deleteTranslation = async (tid) => {
    if (!activeTrack) return;
    if (!window.confirm("Delete this translation?")) return;
    try {
      await api.delete(`/projects/${id}/translations/${tid}?track_id=${encodeURIComponent(activeTrack.track_id)}`);
      if (activeTranslationId === tid) setActiveTranslationId(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const downloadSub = async (format, translationId, includeSpeakers = false) => {
    if (!activeTrack) return;
    try {
      const qs = new URLSearchParams();
      qs.set("track_id", activeTrack.track_id);
      if (translationId) qs.set("translation_id", translationId);
      if (includeSpeakers) qs.set("include_speakers", "true");
      const url = `${API_BASE}/projects/${id}/${format}?${qs.toString()}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const bUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bUrl;
      const t = activeTrack.translations?.find((x) => x.translation_id === translationId);
      const lang = t ? t.target_language : project.source_language;
      const base = activeTrack.title || project.title;
      a.download = `${base}.${lang}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(bUrl);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const editOriginalSegment = async (index, text) => {
    if (!activeTrack) return;
    try {
      await api.patch(`/projects/${id}/segments/${index}?track_id=${encodeURIComponent(activeTrack.track_id)}`, { text });
      setProject((p) => {
        if (!p) return p;
        const tIdx = (p.tracks || []).findIndex((t) => t.track_id === activeTrack.track_id);
        if (tIdx < 0) return p;
        const next = { ...p, tracks: [...p.tracks] };
        const tr = { ...next.tracks[tIdx] };
        tr.segments = [...tr.segments];
        tr.segments[index] = { ...tr.segments[index], text };
        next.tracks[tIdx] = tr;
        return next;
      });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const editTranslationSegment = async (index, text) => {
    if (!activeTranslationId || !activeTrack) return;
    try {
      await api.patch(`/projects/${id}/translations/${activeTranslationId}/segments/${index}?track_id=${encodeURIComponent(activeTrack.track_id)}`, { text });
      setProject((p) => {
        if (!p) return p;
        const tIdx = (p.tracks || []).findIndex((t) => t.track_id === activeTrack.track_id);
        if (tIdx < 0) return p;
        const next = { ...p, tracks: [...p.tracks] };
        const tr = { ...next.tracks[tIdx] };
        tr.translations = (tr.translations || []).map((t) => {
          if (t.translation_id !== activeTranslationId) return t;
          const segs = [...t.segments];
          segs[index] = { ...segs[index], text };
          return { ...t, segments: segs };
        });
        next.tracks[tIdx] = tr;
        return next;
      });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const renameTrack = async (newTitle) => {
    if (!activeTrack) return;
    try {
      await api.patch(`/projects/${id}/tracks/${activeTrack.track_id}`, { title: newTitle });
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const removeTrack = async (trackId) => {
    if (!window.confirm("Remove this file from the project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}/tracks/${trackId}`);
      if (activeTrackId === trackId) setActiveTrackId(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <AppShell><div className="overline text-[--muted]">Loading…</div></AppShell>;
  if (!project) return null;
  const tracks = project.tracks || [];
  const activeTranslation = activeTrack?.translations?.find((t) => t.translation_id === activeTranslationId);
  const aggStatus = (() => {
    if (!tracks.length) return "draft";
    const ss = tracks.map((t) => t.status || "draft");
    if (ss.includes("failed")) return "failed";
    if (ss.includes("transcribing")) return "transcribing";
    if (ss.every((s) => s === "ready")) return "ready";
    return "partial";
  })();

  return (
    <AppShell>
      <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 overline text-[--muted] hover:text-[--ink] mb-6">
        <ArrowLeft weight="bold" size={14} /> Back to studio
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="overline text-[--muted] mb-2">PROJECT</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter" data-testid="project-title">{project.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-mono text-[--muted]">
            <span className="px-2 py-1 border border-[--line]">ID · {project.project_id}</span>
            <span className="px-2 py-1 border border-[--line]">SRC · {project.source_language}{project.source_dialect ? ` · ${project.source_dialect}` : ""}</span>
            <span className="px-2 py-1 border border-[--line] inline-flex items-center gap-2">
              <StatusDot status={aggStatus} /> {aggStatus.toUpperCase()}
            </span>
            <span className="px-2 py-1 border border-[--line]" data-testid="project-tracks-count">{tracks.length} FILE{tracks.length === 1 ? "" : "S"}</span>
            {project.tags?.length > 0 && project.tags.map((t) => (
              <span key={t} className="px-2 py-1 border border-[--line]" data-testid="project-tag">#{t}</span>
            ))}
            {!project.owned && project.owner && (
              <span className="px-2 py-1 border border-[--line]" data-testid="project-shared-by">SHARED BY · {project.owner.email}</span>
            )}
          </div>
        </div>
        {project.owned && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowShare(true)}
              data-testid="project-share-btn"
              className="bg-[--accent] text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent-hi] transition-colors"
            >
              <UsersFour weight="bold" size={14} /> Share
            </button>
            <button
              onClick={() => setShowSettings(true)}
              data-testid="project-settings-btn"
              className="border border-[--ink] px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors"
            >
              <Gear weight="bold" size={14} /> Edit details
            </button>
          </div>
        )}
      </div>

      {/* Track switcher */}
      {tracks.length > 0 && (
        <div className="mb-8" data-testid="track-switcher">
          <div className="overline text-[--muted] mb-3">FILES IN THIS PROJECT</div>
          <div className="flex flex-wrap items-stretch gap-2">
            {tracks.map((t) => (
              <div
                key={t.track_id}
                className={`inline-flex items-stretch border border-[--ink] ${activeTrackId === t.track_id ? "bg-[--ink] text-white" : "bg-white"}`}
                data-testid="track-tab"
              >
                <button
                  onClick={() => setActiveTrackId(t.track_id)}
                  data-testid={`track-tab-${t.track_id}`}
                  className={`px-3 py-2 text-xs font-mono inline-flex items-center gap-2 ${activeTrackId === t.track_id ? "" : "hover:bg-[--paper]"}`}
                  title={t.audio_filename || t.title}
                >
                  <StatusDot status={t.status || "draft"} />
                  <span className="truncate max-w-[200px]">{t.title || t.audio_filename || "Track"}</span>
                  <span className="text-[10px] opacity-60">· {(t.segments || []).length} seg</span>
                </button>
                {project.owned && (
                  <button
                    onClick={() => removeTrack(t.track_id)}
                    data-testid={`track-remove-${t.track_id}`}
                    aria-label="Remove track"
                    className={`px-2 py-2 border-l border-[--ink] ${activeTrackId === t.track_id ? "hover:bg-red-500" : "hover:bg-[--accent] hover:text-white"} transition-colors`}
                  >
                    <X weight="bold" size={12} />
                  </button>
                )}
              </div>
            ))}
            {project.owned && (
              <button
                onClick={() => setShowAddTrack(true)}
                data-testid="add-track-btn"
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold border border-dashed border-[--ink] hover:bg-[--ink] hover:text-white transition-colors"
              >
                <Plus weight="bold" size={12} /> Add file
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 1 — transcribe (or first upload) */}
      {!activeTrack ? (
        <Section num="01" title="Upload & transcribe" desc="Drop an audio file. Add more later — each file becomes a switchable track in this project.">
          <UploadZone onFile={setFile} busy={uploading} />
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer select-none" data-testid="auto-start-checkbox-label">
              <input
                type="checkbox"
                checked={autoStart}
                onChange={(e) => setAutoStart(e.target.checked)}
                disabled={uploading}
                data-testid="auto-start-checkbox"
                className="w-4 h-4 accent-[--accent]"
              />
              Start transcription immediately after upload
            </label>
            <button
              onClick={() => uploadAudio()}
              disabled={!file || uploading}
              data-testid="upload-btn"
              className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {uploading ? `Uploading… ${uploadProgress?.pct ?? 0}%` : "Upload"}
              {!uploading && <Waveform weight="bold" size={16} />}
            </button>
          </div>
          {uploadProgress && <UploadProgressBar progress={uploadProgress} />}
          <JobProgress job={transcribeJob} testId="transcribe-progress" onCancel={cancelJob} />
        </Section>
      ) : (
        <Section num="01" title="Transcribed audio" desc="Switch between files using the tabs above. Each file has its own SRT, translations, and progress.">
          <div className="hard-border bg-white p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Waveform weight="duotone" size={32} />
              <div>
                <div className="font-mono text-sm" data-testid="audio-filename">{activeTrack.audio_filename || activeTrack.title}</div>
                <div className="font-mono text-xs text-[--muted]">
                  {activeTrack.audio_size ? (activeTrack.audio_size / (1024 * 1024)).toFixed(2) : "?"} MB · {(activeTrack.segments || []).length} segments · {(activeTrack.status || "draft").toUpperCase()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(activeTrack.segments || []).length > 0 && (
                <>
                  <button onClick={() => downloadSub("srt", null)} data-testid="download-original-srt-btn" className="border border-[--ink] px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors">
                    <DownloadSimple weight="bold" size={14} /> .srt
                  </button>
                  <button onClick={() => downloadSub("vtt", null)} data-testid="download-original-vtt-btn" className="border border-[--ink] px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors">
                    <DownloadSimple weight="bold" size={14} /> .vtt
                  </button>
                </>
              )}
              {/* Transcribe button — shown when track is uploaded but not yet transcribed, or failed/cancelled */}
              {project.owned && activeTrack.audio_storage_path && ["uploaded", "failed", "cancelled"].includes(activeTrack.status) && (
                <button
                  onClick={() => startTranscribe(activeTrack.track_id)}
                  data-testid="transcribe-btn"
                  className="bg-[--accent] text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent-hi] transition-colors"
                >
                  <Waveform weight="bold" size={14} />
                  {activeTrack.status === "uploaded" ? "Transcribe" : "Retry transcription"}
                </button>
              )}
              {project.owned && activeTrack.audio_storage_path && activeTrack.status === "ready" && (
                <button
                  onClick={() => setShowReprocess(true)}
                  data-testid="reprocess-btn"
                  className="bg-[--ink] text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-2 hover:bg-[--accent] transition-colors"
                >
                  <ArrowsClockwise weight="bold" size={14} /> Reprocess
                </button>
              )}
            </div>
          </div>
          {/* In-flight banner — pulls from track (resumes after refresh) and live WS */}
          <InFlightBanner track={activeTrack} liveJob={transcribeJob} onCancel={cancelJob} />
          {activeTrack.status === "failed" && activeTrack.error && (
            <div className="mt-4 border border-red-500 bg-red-50 text-red-700 p-4 text-xs" data-testid="transcribe-error-banner">
              <div className="font-bold mb-1 font-mono uppercase text-[10px]">Transcription failed</div>
              <div className="font-mono whitespace-pre-wrap break-words">{activeTrack.error}</div>
            </div>
          )}
          {(activeTrack.segments || []).length > 0 && activeTrack.audio_storage_path && (
            <AudioPreview
              testId="original-audio-preview"
              projectId={project.project_id}
              trackId={activeTrack.track_id}
              segments={activeTrack.segments}
              language={project.source_language}
              dialect={project.source_dialect}
              label={`ORIGINAL · ${project.source_language.toUpperCase()}${project.source_dialect ? ` · ${project.source_dialect}` : ""}`}
            />
          )}
        </Section>
      )}

      {/* Step 2 — translate */}
      {(activeTrack?.segments || []).length > 0 && (
        <Section num="02" title="Translate or reshape dialect" desc="Pick a target language for translation, or keep the source language and convert between dialects.">
          <div className="hard-border bg-white p-6 grid md:grid-cols-4 gap-4">
            <div className="md:col-span-1">
              <label className="overline block mb-2">Mode</label>
              <div className="flex border border-[--ink]">
                <button onClick={() => setMode("translate")} data-testid="mode-translate-btn" className={`flex-1 py-3 text-xs font-bold ${mode === "translate" ? "bg-[--ink] text-white" : "hover:bg-[--paper]"}`}>Translate</button>
                <button onClick={() => setMode("dialect")} data-testid="mode-dialect-btn" className={`flex-1 py-3 text-xs font-bold border-l border-[--ink] ${mode === "dialect" ? "bg-[--ink] text-white" : "hover:bg-[--paper]"}`}>Dialect</button>
              </div>
            </div>
            <div className="md:col-span-1">
              <label className="overline block mb-2">Target language</label>
              <select value={targetLang} onChange={(e) => { setTargetLang(e.target.value); setTargetDialect(""); }} disabled={mode === "dialect"} data-testid="target-language-select"
                className="w-full border border-[--ink] px-3 py-3 text-sm bg-white disabled:bg-[--paper] disabled:text-[--muted]">
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name} — {l.native}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">
                {mode === "dialect" ? "Target dialect (required)" : "Target dialect (optional)"}
              </label>
              <select value={targetDialect} onChange={(e) => setTargetDialect(e.target.value)} data-testid="target-dialect-select"
                className="w-full border border-[--ink] px-3 py-3 text-sm bg-white">
                <option value="">{mode === "dialect" ? "— choose a dialect —" : "— neutral / standard —"}</option>
                {dialectsForTarget.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="md:col-span-4 flex items-center justify-end">
              <button
                onClick={translate}
                disabled={isTranslating || (mode === "dialect" && !targetDialect)}
                data-testid="run-translate-btn"
                className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isTranslating ? "Working with Flowscribe-GPEngine…" : (mode === "dialect" ? "Convert dialect" : "Translate")}
                {!isTranslating && <Translate weight="bold" size={16} />}
              </button>
            </div>
            <div className="md:col-span-4"><JobProgress job={translateJob} testId="translate-progress" onCancel={cancelJob} /></div>
          </div>

          {activeTrack?.translations?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {activeTrack.translations.map((t) => (
                <div key={t.translation_id} className="inline-flex items-center border border-[--ink]">
                  <button onClick={() => setActiveTranslationId(t.translation_id)} data-testid="translation-tab"
                    className={`px-3 py-2 text-xs font-mono ${activeTranslationId === t.translation_id ? "bg-[--ink] text-white" : "hover:bg-[--paper]"}`}>
                    {t.mode === "dialect" ? "DIALECT" : "TR"} · {t.target_language}
                    {t.target_dialect ? ` · ${t.target_dialect.split(" ")[0]}` : ""}
                  </button>
                  <button onClick={() => downloadSub("srt", t.translation_id)} className="px-2 py-2 border-l border-[--ink] hover:bg-[--ink] hover:text-white transition-colors" aria-label="Download SRT" data-testid="download-translation-srt-btn">
                    <DownloadSimple weight="bold" size={14} />
                  </button>
                  <button onClick={() => downloadSub("vtt", t.translation_id)} className="px-2 py-2 border-l border-[--ink] hover:bg-[--ink] hover:text-white transition-colors" aria-label="Download VTT" data-testid="download-translation-vtt-btn">
                    <span className="text-[10px] font-mono">VTT</span>
                  </button>
                  <button onClick={() => deleteTranslation(t.translation_id)} className="px-2 py-2 border-l border-[--ink] hover:bg-[--accent] hover:text-white transition-colors" aria-label="Delete" data-testid="delete-translation-btn">
                    <Trash weight="bold" size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {activeTranslation && activeTrack?.audio_storage_path && (
            <AudioPreview
              testId="translation-audio-preview"
              projectId={project.project_id}
              trackId={activeTrack.track_id}
              segments={activeTranslation.segments}
              language={activeTranslation.target_language}
              dialect={activeTranslation.target_dialect}
              label={`${activeTranslation.mode === "dialect" ? "DIALECT" : "TRANSLATION"} · ${activeTranslation.target_language.toUpperCase()}${activeTranslation.target_dialect ? ` · ${activeTranslation.target_dialect.split(" ")[0]}` : ""}`}
            />
          )}
        </Section>
      )}

      {/* Step 3 — diarize (admin-gated feature flag) */}
      {featureDiarize && (activeTrack?.segments || []).length > 0 && (
        <Section num="03" title="Speaker diarization (optional)" desc="Label each segment with a speaker. Choose prompt-based (fast) or pyannote.ai cloud (acoustic, best accuracy).">
          <div className="hard-border bg-white p-6 grid md:grid-cols-4 gap-4">
            <div className="md:col-span-1">
              <label className="overline block mb-2">Method</label>
              <div className="flex border border-[--ink]">
                <button onClick={() => setDiarizeMethod("prompt")} data-testid="diarize-method-prompt-btn" className={`flex-1 py-3 text-xs font-bold ${diarizeMethod === "prompt" ? "bg-[--ink] text-white" : "hover:bg-[--paper]"}`}>Prompt</button>
                <button onClick={() => setDiarizeMethod("pyannote_cloud")} data-testid="diarize-method-pyannote-btn" className={`flex-1 py-3 text-xs font-bold border-l border-[--ink] ${diarizeMethod === "pyannote_cloud" ? "bg-[--ink] text-white" : "hover:bg-[--paper]"}`}>Acoustic</button>
              </div>
            </div>
            <div className="md:col-span-3 text-xs text-[--muted] leading-relaxed self-end">
              {diarizeMethod === "prompt"
                ? "Uses Flowscribe's neural engine to infer speaker turns from the transcript alone — no extra keys needed."
                : "Real acoustic diarization powered by Flowscribe-DEngine. Requires the diarization key to be configured on the server."}
            </div>
            <div className="md:col-span-4 flex items-center justify-end">
              <button onClick={diarize} disabled={isDiarizing} data-testid="run-diarize-btn"
                className="bg-[--ink] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent] transition-colors disabled:opacity-50 inline-flex items-center gap-2">
                {isDiarizing ? "Labeling speakers…" : `Diarize ${activeTranslation ? "translation" : "original"}`}
                {!isDiarizing && <UsersFour weight="bold" size={16} />}
              </button>
            </div>
            <div className="md:col-span-4"><JobProgress job={diarizeJob} testId="diarize-progress" onCancel={cancelJob} /></div>
          </div>
        </Section>
      )}

      {/* Step 4 — SRT viewer */}
      {(activeTrack?.segments || []).length > 0 && (
        <Section num="04" title="Review & edit" desc="Double-click any line to edit. Side-by-side compare with your translation. Arabic, Hebrew, Persian, and Urdu render right-to-left natively.">
          <div className="grid lg:grid-cols-2 gap-px bg-[--ink] border border-[--ink]">
            <SRTViewer
              title="ORIGINAL"
              testId="original-srt-viewer"
              segments={activeTrack.segments}
              language={project.source_language}
              dialect={project.source_dialect}
              onEdit={editOriginalSegment}
            />
            <SRTViewer
              title={activeTranslation ? (activeTranslation.mode === "dialect" ? "DIALECT" : "TRANSLATION") : "TRANSLATION"}
              testId="translated-srt-viewer"
              segments={activeTranslation?.segments || []}
              language={activeTranslation?.target_language || project.source_language}
              dialect={activeTranslation?.target_dialect}
              variant="inverse"
              onEdit={activeTranslation ? editTranslationSegment : null}
            />
          </div>
        </Section>
      )}

      {/* Step 5 — burn in */}
      {(activeTrack?.segments || []).length > 0 && (
        <Section num="05" title="Burn subtitles into video (optional)" desc="Upload the matching video. We hardcode the selected track via ffmpeg and give you a downloadable mp4.">
          <div className="hard-border bg-white p-6">
            <div className="mb-4 overline text-[--muted]">
              Selected track: {activeTranslation ? `${activeTranslation.target_language.toUpperCase()}${activeTranslation.target_dialect ? ` · ${activeTranslation.target_dialect.split(" ")[0]}` : ""}` : `ORIGINAL · ${project.source_language.toUpperCase()}`}
            </div>
            <input
              type="file"
              accept="video/*"
              data-testid="burn-video-input"
              onChange={(e) => setVideoFile(e.target.files?.[0])}
              className="block w-full text-sm font-mono border border-[--ink] p-3 bg-[--paper] file:mr-4 file:px-4 file:py-2 file:border file:border-[--ink] file:bg-white file:font-bold file:hover:bg-[--ink] file:hover:text-white file:cursor-pointer"
            />
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-[--muted] font-mono">
                {videoFile ? `${videoFile.name} — ${(videoFile.size / (1024 * 1024)).toFixed(1)} MB` : "No video selected"}
              </div>
              <button
                onClick={burn}
                disabled={!videoFile || isBurning}
                data-testid="burn-btn"
                className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isBurning ? "Rendering with ffmpeg…" : "Render mp4"}
                {!isBurning && <FilmSlate weight="bold" size={16} />}
              </button>
            </div>
            <JobProgress job={burnJob} testId="burn-progress" onCancel={cancelJob} />
            {burnLink && (
              <a
                href={burnLink.url}
                download={burnLink.filename}
                data-testid="burn-download-link"
                className="mt-4 inline-flex items-center gap-2 bg-[--ink] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent] transition-colors"
              >
                <DownloadSimple weight="bold" size={16} />
                Download {burnLink.filename}
              </a>
            )}
          </div>
        </Section>
      )}
      {showSettings && (
        <ProjectSettingsModal
          project={project}
          onClose={() => setShowSettings(false)}
          onSaved={(p) => { setProject((prev) => ({ ...prev, ...p })); }}
        />
      )}
      {showShare && (
        <ShareProjectModal
          project={project}
          onClose={() => setShowShare(false)}
          onChanged={(p) => { setProject((prev) => ({ ...prev, ...p })); }}
        />
      )}
      {showReprocess && activeTrack && (
        <ReprocessModal
          project={project}
          track={activeTrack}
          onClose={() => setShowReprocess(false)}
          onStarted={() => { setShowReprocess(false); load(); }}
        />
      )}
      {showAddTrack && (
        <AddTrackModal
          onClose={() => setShowAddTrack(false)}
          onUpload={async (f, title, optAuto) => {
            const res = await uploadAudio(f, title, { autoStart: optAuto });
            if (res) setShowAddTrack(false);
          }}
        />
      )}
    </AppShell>
  );
}

function Section({ num, title, desc, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-start gap-4 mb-5">
        <div className="font-mono font-black text-5xl sm:text-6xl text-[--muted] leading-none">{num}</div>
        <div className="pt-2">
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter">{title}</h2>
          {desc && <p className="text-sm text-[--muted] mt-1 max-w-2xl">{desc}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function StatusDot({ status }) {
  const map = {
    ready: "bg-emerald-500",
    transcribing: "bg-amber-500 animate-pulse",
    failed: "bg-red-500",
    partial: "bg-sky-500",
    queued: "bg-amber-500 animate-pulse",
    uploaded: "bg-sky-500",
    draft: "bg-[--muted]",
  };
  return <span className={`w-2 h-2 inline-block ${map[status] || map.draft}`}></span>;
}


function UploadProgressBar({ progress }) {
  const { pct = 0, loaded = 0, total = 0 } = progress || {};
  return (
    <div className="mt-4 hard-border bg-white p-4" data-testid="upload-progress-bar">
      <div className="flex items-center justify-between font-mono text-xs text-[--muted] mb-2">
        <span>UPLOADING…</span>
        <span>{pct}% · {(loaded / (1024 * 1024)).toFixed(1)} / {(total / (1024 * 1024)).toFixed(1)} MB</span>
      </div>
      <div className="w-full h-2 bg-[--paper] border border-[--line]">
        <div
          className="h-full bg-[--accent] transition-all duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


function InFlightBanner({ track, liveJob, onCancel }) {
  // Prefer the latest WebSocket event; fall back to the persisted last_job_*.
  const status = liveJob?.status || (
    ["queued", "transcribing"].includes(track?.status) ? "running" : null
  );
  if (!status || status === "done") return null;
  const pct = Math.round(((liveJob?.progress ?? track?.last_job_progress) || 0) * 100);
  const msg = liveJob?.message || track?.last_job_message || (track?.status === "queued" ? "Queued…" : "Transcribing…");
  const jobId = liveJob?.job_id || track?.last_job_id;
  const isFailed = status === "failed";
  const isCancelled = status === "cancelled";
  const isRunning = ["queued", "running"].includes(status);
  return (
    <div className={`mt-4 hard-border ${isFailed ? "bg-red-50 border-red-500" : isCancelled ? "bg-gray-50" : "bg-white"} p-4`} data-testid="inflight-banner">
      <div className="flex items-center justify-between font-mono text-xs mb-2">
        <span className={isFailed ? "text-red-700 font-bold" : "text-[--muted]"}>
          {isFailed ? "FAILED" : isCancelled ? "CANCELLED" : "TRANSCRIBING"} {jobId && <span className="opacity-60">· {jobId.slice(0, 12)}…</span>}
        </span>
        <div className="flex items-center gap-3">
          {isRunning && <span className="text-[--muted]">{pct}%</span>}
          {isRunning && onCancel && jobId && (
            <button
              type="button"
              onClick={() => onCancel(jobId)}
              data-testid="inflight-cancel-btn"
              className="border border-[--ink] px-2 py-1 text-[10px] font-bold hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors inline-flex items-center gap-1"
              title="Cancel transcription"
            >
              <X weight="bold" size={10} /> CANCEL
            </button>
          )}
        </div>
      </div>
      {isRunning && (
        <div className="w-full h-2 bg-[--paper] border border-[--line] mb-2">
          <div className="h-full bg-[--accent] transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className={`text-xs ${isFailed ? "text-red-700 font-mono whitespace-pre-wrap" : "text-[--muted]"}`}>{msg}</div>
    </div>
  );
}

function JobProgress({ job, testId, onCancel }) {
  if (!job) return null;
  const pct = Math.min(100, Math.round((job.progress || 0) * 100));
  const running = ["queued", "running"].includes(job.status);
  const colorMap = { failed: "bg-red-500", done: "bg-emerald-500", cancelled: "bg-gray-400" };
  const fillClass = colorMap[job.status] || "bg-[--ink]";
  return (
    <div className="mt-4" data-testid={testId}>
      <div className="flex items-center justify-between text-xs font-mono text-[--muted] mb-2">
        <span className="uppercase tracking-widest">{job.job_type} · {job.status}</span>
        <div className="flex items-center gap-3">
          <span>{pct}%</span>
          {running && onCancel && (
            <button
              type="button"
              onClick={() => onCancel(job.job_id)}
              data-testid={`${testId}-cancel-btn`}
              className="border border-[--ink] px-2 py-1 text-[10px] font-bold hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors inline-flex items-center gap-1"
              title="Cancel this job"
            >
              <X weight="bold" size={10} /> CANCEL
            </button>
          )}
        </div>
      </div>
      <div className="h-[6px] bg-black/10 relative overflow-hidden">
        {running && pct < 5 ? (
          <div className="progress-fill"></div>
        ) : (
          <div className={`h-full ${fillClass} transition-[width] duration-500`} style={{ width: `${pct}%` }}></div>
        )}
      </div>
      {job.message && <div className="mt-2 text-xs text-[--muted]">{job.message}</div>}
      {job.error && <div className="mt-2 text-xs text-red-600 font-mono">{job.error}</div>}
    </div>
  );
}


function useEscape(onEscape) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onEscape(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onEscape]);
}


function ProjectSettingsModal({ project, onClose, onSaved }) {
  useEscape(onClose);
  const [title, setTitle] = useState(project.title || "");
  const [tagsInput, setTagsInput] = useState((project.tags || []).join(", "));
  const [sourceLang, setSourceLang] = useState(project.source_language || "ar");
  const [sourceDialect, setSourceDialect] = useState(project.source_dialect || "");
  const [shareEmail, setShareEmail] = useState("");
  const [shared, setShared] = useState(project.shared_users || []);
  const [busy, setBusy] = useState(false);
  const [busyShare, setBusyShare] = useState(false);
  const sourceDialectOptions = DIALECTS[sourceLang] || [];
  const langChanged = sourceLang !== project.source_language || (sourceDialect || "") !== (project.source_dialect || "");
  const hasTranscriptions = (project.tracks || []).some((t) => (t.segments || []).length > 0);

  const saveDetails = async () => {
    setBusy(true);
    try {
      const tagList = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      const { data } = await api.patch(`/projects/${project.project_id}`, {
        title: title.trim() || project.title,
        tags: tagList,
        source_language: sourceLang,
        source_dialect: sourceDialect || "",
      });
      toast.success("Project updated");
      onSaved({
        title: data.title,
        tags: data.tags,
        source_language: data.source_language,
        source_dialect: data.source_dialect,
      });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const addShare = async (e) => {
    e.preventDefault();
    const email = shareEmail.trim().toLowerCase();
    if (!email) return;
    setBusyShare(true);
    try {
      const { data } = await api.post(`/projects/${project.project_id}/share`, { emails: [email] });
      if (data.missing_emails?.length) {
        toast.error(`Not registered: ${data.missing_emails.join(", ")}`);
      }
      // Refetch project to get expanded shared_users
      const { data: fresh } = await api.get(`/projects/${project.project_id}`);
      setShared(fresh.shared_users || []);
      onSaved({ shared_users: fresh.shared_users, shared_with: fresh.shared_with });
      setShareEmail("");
      if (!data.missing_emails?.length) toast.success("Shared");
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusyShare(false);
    }
  };

  const removeShare = async (uid) => {
    try {
      await api.delete(`/projects/${project.project_id}/share/${uid}`);
      const next = shared.filter((u) => u.user_id !== uid);
      setShared(next);
      onSaved({ shared_users: next, shared_with: next.map((u) => u.user_id) });
      toast.success("Removed");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white hard-border max-w-xl w-full p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="project-settings-modal">
        <div className="overline text-[--muted] mb-2">PROJECT SETTINGS</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-6">Edit details & sharing</h2>
        <div className="space-y-5">
          <div>
            <label className="overline block mb-2">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="project-title-edit-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
            />
          </div>
          <div>
            <label className="overline block mb-2">Tags (comma-separated)</label>
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              data-testid="project-tags-edit-input"
              placeholder="podcast, season-2"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Source language</label>
              <select
                value={sourceLang}
                onChange={(e) => { setSourceLang(e.target.value); setSourceDialect(""); }}
                data-testid="project-source-language-select"
                className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent]"
              >
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name} — {l.native}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Source dialect</label>
              <select
                value={sourceDialect}
                onChange={(e) => setSourceDialect(e.target.value)}
                data-testid="project-source-dialect-select"
                disabled={sourceDialectOptions.length === 0}
                className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent] disabled:bg-[--paper] disabled:text-[--muted]"
              >
                <option value="">— none / mixed —</option>
                {sourceDialectOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          {langChanged && hasTranscriptions && (
            <div className="border border-amber-500 bg-amber-50 text-amber-800 text-xs p-3 font-mono">
              Existing transcriptions were generated with the previous language. Click <strong>Reprocess</strong> on each file after saving to regenerate them with the new language.
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={saveDetails}
              disabled={busy}
              data-testid="save-project-details-btn"
              className="bg-[--ink] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent] transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save details"}
            </button>
          </div>
          <hr className="border-[--line]" />
          <div>
            <div className="overline mb-2">Share with users</div>
            <p className="text-xs text-[--muted] mb-3">Enter the email of an existing Flowscribe user. They'll be able to view and edit this project.</p>
            <form onSubmit={addShare} className="flex gap-2">
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="user@example.com"
                data-testid="share-email-input"
                className="flex-1 border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
              />
              <button
                type="submit"
                disabled={busyShare || !shareEmail.trim()}
                data-testid="share-add-btn"
                className="bg-[--accent] text-white px-4 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
              >
                {busyShare ? "…" : "Share"}
              </button>
            </form>
            {shared.length === 0 ? (
              <div className="text-xs text-[--muted] font-mono mt-3">Not shared with anyone.</div>
            ) : (
              <ul className="mt-3 divide-y divide-[--line] border border-[--line]">
                {shared.map((u) => (
                  <li key={u.user_id} className="flex items-center justify-between p-3" data-testid="shared-user-row">
                    <div>
                      <div className="text-sm font-semibold">{u.name || u.email}</div>
                      <div className="text-[11px] font-mono text-[--muted]">{u.email}</div>
                    </div>
                    <button
                      onClick={() => removeShare(u.user_id)}
                      data-testid="share-remove-btn"
                      className="border border-[--ink] px-3 py-2 text-xs font-bold hover:bg-[--accent] hover:text-white hover:border-[--accent] transition-colors"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReprocessModal({ project, track, onClose, onStarted }) {
  useEscape(onClose);
  const [engine, setEngine] = useState(project.stt_engine || "whisper");
  const [lang, setLang] = useState(project.source_language || "en");
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) { console.warn("[reprocess] failed to fetch settings", err); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Reprocess will replace existing segments and remove all existing translations for this file. Continue?")) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.project_id}/reprocess`, {
        track_id: track?.track_id,
        stt_engine: engine,
        source_language: lang,
      });
      toast.success("Reprocess started");
      onStarted();
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="reprocess-modal">
        <div className="overline text-[--muted] mb-2">REPROCESS</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-2">Re-run transcription</h2>
        <p className="text-xs text-[--muted] mb-1 font-mono">FILE · {track?.title || track?.audio_filename || "—"}</p>
        <p className="text-xs text-[--muted] mb-5">Uses the original audio that's already stored. You can switch the engine and language. Existing translations for this file will be cleared.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline block mb-2">Source language</label>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="reprocess-language-select"
              className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent]"
            >
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name} — {l.native}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">STT engine</label>
            <select
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              data-testid="reprocess-engine-select"
              className="w-full border border-[--ink] px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[--accent]"
            >
              {(settings?.engines || [{ id: "whisper", name: "Flowscribe-WEngine", available: true }]).map((e) => (
                <option key={e.id} value={e.id} disabled={!e.available}>
                  {e.name}{e.available ? "" : " — needs API key"}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="reprocess-submit-btn"
              className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Starting…" : "Reprocess"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function AddTrackModal({ onClose, onUpload }) {
  useEscape(onClose);
  const [pickedFile, setPickedFile] = useState(null);
  const [trackTitle, setTrackTitle] = useState("");
  const [autoStart, setAutoStart] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pickedFile) { toast.error("Pick an audio file"); return; }
    setBusy(true);
    try {
      await onUpload(pickedFile, trackTitle.trim() || null, autoStart);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white hard-border max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="add-track-modal">
        <div className="overline text-[--muted] mb-2">ADD FILE TO PROJECT</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-2">New track</h2>
        <p className="text-xs text-[--muted] mb-5">Adds another audio file (e.g., another episode) to this project. It uses the project's source language and STT engine.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline block mb-2">Audio file</label>
            <input
              type="file"
              accept=".mp3,.wav,.m4a,.mp4,.webm,.mpeg,.mpga,audio/*"
              onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
              data-testid="add-track-file-input"
              className="block w-full text-sm font-mono border border-[--ink] p-3 bg-[--paper] file:mr-4 file:px-4 file:py-2 file:border file:border-[--ink] file:bg-white file:font-bold file:hover:bg-[--ink] file:hover:text-white file:cursor-pointer"
            />
            {pickedFile && (
              <div className="mt-2 text-[11px] font-mono text-[--muted]">
                {pickedFile.name} · {(pickedFile.size / (1024 * 1024)).toFixed(1)} MB
              </div>
            )}
          </div>
          <div>
            <label className="overline block mb-2">Track name (optional)</label>
            <input
              value={trackTitle}
              onChange={(e) => setTrackTitle(e.target.value)}
              placeholder="Episode 03"
              data-testid="add-track-title-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoStart}
              onChange={(e) => setAutoStart(e.target.checked)}
              disabled={busy}
              data-testid="add-track-auto-start"
              className="w-4 h-4 accent-[--accent]"
            />
            Start transcription immediately after upload
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 text-sm font-semibold hover:text-[--accent]">Cancel</button>
            <button
              type="submit"
              disabled={busy || !pickedFile}
              data-testid="add-track-submit-btn"
              className="bg-[--accent] text-white px-5 py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Uploading…" : (autoStart ? "Upload & transcribe" : "Upload only")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
